import React, { useState, useRef, useEffect } from "react";
import { Upload, Search, Sparkles, X, Loader2, ImageIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const Tab = ({ active, onClick, icon: Icon, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex-1 flex items-center justify-center gap-2 py-3 text-[14px] font-semibold transition-all
      ${active ? "text-white border-b-2 border-[#FF5C00]" : "text-neutral-500 border-b-2 border-transparent hover:text-neutral-300"}`}>
    <Icon size={16} /> {children}
  </button>
);

const MediaCenterModal = ({ open, onClose, onSelect }) => {
  const [tab, setTab] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [preview, setPreview] = useState(null); // { url, alt, path }
  const [status, setStatus] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/spotlight/media/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, [open]);

  if (!open) return null;

  const commit = (payload) => {
    onSelect(payload);
    setPreview(null); setResults([]); setPrompt(""); setQuery("");
    onClose();
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/spotlight/media/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      commit({ url: data.url, alt: file.name.replace(/\.[^.]+$/, "") });
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const searchDisabled = status && status.unsplash && !status.unsplash.configured;

  const handleSearch = async () => {
    if (!query.trim()) return;
    if (searchDisabled) return;
    setBusy(true);
    try {
      const { data } = await api.get("/admin/spotlight/media/search", { params: { q: query, per_page: 12 } });
      setResults(data.results || []);
      if ((data.results || []).length === 0) toast.info("No results — try another query");
    } catch (e) {
      const status = e.response?.status;
      if (status === 503) {
        toast.error(e.response?.data?.detail || "Search is not configured yet.");
      } else {
        toast.error(e.response?.data?.detail || "Search failed");
      }
    } finally { setBusy(false); }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/spotlight/media/generate", { prompt });
      setPreview({ url: data.url, alt: prompt.slice(0, 100), path: data.path });
      toast.success("Image generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="media-center-modal">
      <div className="bg-[#111] border border-[#2a2a2a] rounded-2xl w-full max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2a]">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-[#FF7A2F]" />
            <h3 className="text-white text-[16px] font-semibold">Media center</h3>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white p-1" data-testid="media-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex border-b border-[#2a2a2a] bg-[#161616]">
          <Tab active={tab === "upload"} onClick={() => setTab("upload")} icon={Upload} testid="tab-upload">Upload</Tab>
          <Tab active={tab === "search"} onClick={() => setTab("search")} icon={Search} testid="tab-search">Search</Tab>
          <Tab active={tab === "generate"} onClick={() => setTab("generate")} icon={Sparkles} testid="tab-generate">Generate</Tab>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {tab === "upload" && (
            <div>
              <div onClick={() => fileRef.current?.click()}
                data-testid="upload-dropzone"
                className="border-2 border-dashed border-[#333] hover:border-[#FF5C00] rounded-2xl p-14 text-center cursor-pointer transition-colors">
                <Upload size={40} className="text-neutral-500 mx-auto mb-3" />
                <p className="text-white font-medium text-[15px]">Choose an image from your computer</p>
                <p className="text-neutral-500 text-[13px] mt-1">JPEG, PNG or WebP · up to 10 MB</p>
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                     className="hidden" data-testid="upload-input"
                     onChange={(e) => handleUpload(e.target.files?.[0])} />
              {busy && <div className="mt-6 flex items-center gap-2 text-neutral-400"><Loader2 size={16} className="animate-spin" /> Uploading...</div>}
            </div>
          )}

          {tab === "search" && (
            <div>
              {searchDisabled && (
                <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="unsplash-disabled-banner">
                  <Search size={18} className="text-amber-300 mt-0.5 shrink-0" />
                  <div className="flex-1 text-[13px] text-amber-100 leading-relaxed">
                    <div className="font-semibold text-white mb-1">Stock-photo search is disabled</div>
                    <div>{status?.unsplash?.message}</div>
                    <a href={status?.unsplash?.get_key_url || "https://unsplash.com/developers"}
                       target="_blank" rel="noreferrer"
                       className="mt-2 inline-flex items-center gap-1 text-[#FF7A2F] hover:underline"
                       data-testid="unsplash-get-key-link">
                      Get a free Unsplash key <ExternalLink size={11} />
                    </a>
                    <div className="text-amber-200/80 mt-2">In the meantime, use <b>Upload</b> or <b>AI Generate</b> above.</div>
                  </div>
                </div>
              )}
              <div className="flex gap-2 mb-4">
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !searchDisabled && handleSearch()}
                  placeholder="Try: DJ crowd, wedding, corporate event..."
                  data-testid="search-input"
                  disabled={searchDisabled}
                  className="flex-1 bg-[#181818] border border-[#2a2a2a] rounded-lg px-4 py-2.5 text-white placeholder:text-neutral-600 outline-none focus:border-[#FF5C00] disabled:opacity-50 disabled:cursor-not-allowed" />
                <button onClick={handleSearch} disabled={busy || !query.trim() || searchDisabled}
                  data-testid="search-button"
                  className="px-5 py-2.5 rounded-lg bg-[#1A8A4A] hover:bg-[#20A458] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold flex items-center gap-2">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Search
                </button>
              </div>
              {results.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {results.map((r) => (
                    <button key={r.id} onClick={() => commit({ url: r.url, alt: r.alt || query })}
                      data-testid={`search-result-${r.id}`}
                      className="group relative rounded-lg overflow-hidden border border-[#2a2a2a] hover:border-[#FF5C00] transition-colors">
                      <img src={r.thumb} alt={r.alt || query}
                        loading="lazy"
                        className="w-full aspect-[4/3] object-cover" />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2 text-left">
                        <div className="text-[11px] text-white/90 line-clamp-2">
                          Photo by {r.author}
                          {r.author_url && <ExternalLink size={10} className="inline ml-1" />}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {results.length === 0 && !busy && (
                <p className="text-neutral-500 text-[13px] mt-4">Powered by Unsplash. Attribution auto-embedded in the image alt text.</p>
              )}
            </div>
          )}

          {tab === "generate" && (
            <div>
              <label className="block text-[13px] text-neutral-400 mb-2">Describe the image</label>
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
                rows={3} placeholder="A stylish DJ playing at a rooftop bar in Stockholm at golden hour, warm lighting, cinematic"
                data-testid="generate-prompt"
                className="w-full bg-[#181818] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white placeholder:text-neutral-600 outline-none focus:border-[#FF5C00] resize-none" />
              <div className="flex justify-end mt-3">
                <button onClick={handleGenerate} disabled={busy || !prompt.trim()}
                  data-testid="generate-button"
                  className="px-5 py-2.5 rounded-lg bg-[#1A8A4A] hover:bg-[#20A458] disabled:opacity-50 text-white font-semibold flex items-center gap-2">
                  {busy ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : <><Sparkles size={16} /> Generate</>}
                </button>
              </div>
              {preview && (
                <div className="mt-6">
                  <img src={preview.url} alt={preview.alt} className="w-full rounded-xl border border-[#2a2a2a]" />
                  <div className="flex justify-end gap-2 mt-3">
                    <button onClick={() => setPreview(null)} className="px-4 py-2 rounded-lg border border-[#2a2a2a] text-neutral-300 hover:border-[#3a3a3a] text-[14px]">
                      Regenerate
                    </button>
                    <button onClick={() => commit(preview)} data-testid="generate-use"
                      className="px-5 py-2 rounded-lg bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold text-[14px]">
                      Use this image
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaCenterModal;
